import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAssignedContacts,
  updateAssignedContact,
  addAssignedContact,
  removeAssignedContact,
  setContactOptions,
  setContactsLoading,
} from "../../../../../redux/mgmtCo/contactsSlice";
import {
  fetchAssignedContacts,
  fetchAllContacts,
  updateContact,
  updateAssignedContacts as updateAssignedContactsService,
} from "../services/managementContactService";

const useManagementContacts = (managementId) => {
  const dispatch = useDispatch();
  const assignedContacts =
    useSelector((state) => state.managementContacts.assignedContacts) || [];
  const contactOptions =
    useSelector((state) => state.managementContacts.contactOptions) || [];
  const loading = useSelector((state) => state.managementContacts.loading);

  const loadContacts = useCallback(async () => {
    if (!managementId) return;
    dispatch(setContactsLoading(true));
    try {
      const [allContacts, assigned] = await Promise.all([
        fetchAllContacts(),
        fetchAssignedContacts(managementId),
      ]);
      dispatch(
        setContactOptions(Array.isArray(allContacts) ? allContacts : [])
      );
      // Filter and enrich assigned contacts.
      const filteredAssigned = Array.isArray(assigned)
        ? assigned.filter(
            (item) =>
              item.mainContact || item.agentContact || item.collectionsContact
          )
        : [];
      const enrichedAssigned = filteredAssigned.map((assignedContact) => {
        const fullContact = allContacts.find(
          (c) => c.contactID === assignedContact.contactId
        );
        return fullContact
          ? {
              ...fullContact,
              ...assignedContact,
              contactID: assignedContact.contactId,
            }
          : assignedContact;
      });
      dispatch(setAssignedContacts(enrichedAssigned));
    } catch (error) {
      console.error("Error loading contacts:", error);
      dispatch(setAssignedContacts([]));
    } finally {
      dispatch(setContactsLoading(false));
    }
  }, [managementId, dispatch]);

  useEffect(() => {
    if (managementId) loadContacts();
  }, [managementId, loadContacts]);

  const handleRowFieldChange = useCallback(
    (contactID, field, value) => {
      // Local update logic (dispatch updateAssignedContact)
      dispatch(
        updateAssignedContact({ contactID, updates: { [field]: value } })
      );
    },
    [dispatch]
  );

  const handleRowAutoSave = useCallback(
    async (contactID) => {
      const contact = assignedContacts.find((c) => c.contactID === contactID);
      if (!contact || !contact.contactID) return;
      try {
        await updateContact(contactID, contact);
      } catch (error) {
        console.error("Error auto-saving contact:", error);
      }
    },
    [assignedContacts]
  );

  const addContactForRole = useCallback(
    async (roleKey, contactID) => {
      if (!contactID) {
        alert("Please select a contact to add.");
        return;
      }
      const selected = contactOptions.find(
        (c) => c.contactID.toString() === contactID.toString()
      );
      if (!selected) {
        console.error("Selected contact not found in options");
        return;
      }
      const existingContact = assignedContacts.find(
        (c) => c.contactID.toString() === contactID.toString()
      );
      if (existingContact) {
        if (existingContact[roleKey]) {
          alert(`Contact is already assigned as ${roleKey}.`);
          return;
        } else {
          const updatedContact = { ...existingContact, [roleKey]: true };
          dispatch(
            updateAssignedContact({
              contactID,
              updates: { [roleKey]: true },
            })
          );
          const updatedContacts = assignedContacts.map((c) =>
            c.contactID.toString() === contactID.toString() ? updatedContact : c
          );
          try {
            await updateAssignedContactsService(managementId, updatedContacts);
          } catch (error) {
            console.error("Error updating assigned contacts on server:", error);
          }
          return;
        }
      }
      const newContact = {
        ...selected,
        managementCompanyId: managementId,
        mainContact: roleKey === "mainContact",
        agentContact: roleKey === "agentContact",
        collectionsContact: roleKey === "collectionsContact",
        isEditing: false,
        roleKey,
      };
      dispatch(addAssignedContact(newContact));
      try {
        await updateAssignedContactsService(managementId, [
          ...assignedContacts,
          newContact,
        ]);
      } catch (error) {
        console.error("Error updating assigned contacts on server:", error);
      }
    },
    [contactOptions, assignedContacts, managementId, dispatch]
  );

  const removeContactForRole = useCallback(
    async (roleKey, contactID) => {
      const existing = assignedContacts.find((c) => c.contactID === contactID);
      if (!existing) return;
      const updatedContact = { ...existing, [roleKey]: false };
      if (
        !updatedContact.mainContact &&
        !updatedContact.agentContact &&
        !updatedContact.collectionsContact
      ) {
        dispatch(removeAssignedContact(contactID));
      } else {
        dispatch(
          updateAssignedContact({
            contactID,
            updates: { [roleKey]: false },
          })
        );
      }
      const updatedContacts = assignedContacts
        .map((c) => (c.contactID === contactID ? updatedContact : c))
        .filter((c) => c.mainContact || c.agentContact || c.collectionsContact);
      try {
        await updateAssignedContactsService(managementId, updatedContacts);
      } catch (error) {
        console.error("Error updating assigned contacts on server:", error);
      }
    },
    [assignedContacts, managementId, dispatch]
  );

  return {
    assignedContacts,
    contactOptions,
    loading,
    handleRowFieldChange,
    handleRowAutoSave,
    addContactForRole,
    removeContactForRole,
  };
};

export default useManagementContacts;
